import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, NgZone, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BullhornNamespace, InveniasNamespace, SalesforceNamespace } from 'datasource-service-lib';
import { FilePreviewModalComponent } from 'file-preview-lib';
import { IAttachmentMergeFile, IEntityMetadata, INamedEntity } from 'interfaces-lib';
import { FilePreviewHelperService } from 'file-preview-lib';
import { MessageNotificationService } from 'message-notification-service-lib';
import { TransformBytesToMB } from 'pipes-lib';
import { SharedServiceProviderServiceFactory } from 'shared-service-provider-lib';
import { IdentityStorage } from 'storage-service-lib';
import { ContentTypeMapperService, FileExtensionMatcherService } from 'utils-service-lib';
import { runtimeEnvironment } from '../../../../../environments/environment';
import { SimpleTableComponent } from '../../../../common-components/simple-table/simple-table.component';
import { ActionMeta, ISimpleTableColumn, ISimpleTableRow } from '../../../../common-components/simple-table/simple-table.interfaces';
import { IFileListProvider } from '../../../../interfaces/ifilelistprovider';
import { ServiceProviderService } from '../../../../services/service.provider.service';
import { AttachFileTableBaseComponent } from '../base/attach-file-table-base.component';
import {
  DatasourceColumnProviderFactory,
  DatasourceTableIdentifier,
  IDatasourceCellBuilderArgs,
} from './column-providers/datasource-columns.provider';

export interface IAttachFileDialogDataSingleEntity extends IAttachFileDialogData {
  entity: INamedEntity;
}

export interface IAttachFileDialogDataMultiEntity extends IAttachFileDialogData {
  entities: INamedEntity[];
}

export interface IAttachFileDialogData {
  templateName: string;
  templateNamespace: string;
  useFileAttachmentAsTemplate: boolean;
  fileListProvider: IFileListProvider;
  sectionConfig: IFileSectionConfig;
}

export interface IFileSectionConfig {
  required: boolean;
  optional?: boolean;
  attachmentUploadSource?: string;
  attachmentSourceEntities?: string[];
  filters: { extension: string[]; documentTypeId?: string[]; fileMaxAgeDays?: number };
  multiple: boolean;
}

export interface IFileInfo {
  id: string;
  name: string;
  type: string;
  dateModified?: number;
  size?: number;
  source?: IFileSource;
  namespace: string;
  path: string[];
  file?: File;
}

export interface IFileSource {
  id: string;
  entity: string;
}

@Component({
  selector: 'app-datasource-attach-file',
  templateUrl: './datasource-attach-file.component.html',
  styleUrl: './datasource-attach-file.component.scss',
  standalone: false,
})
export class DatasourceAttachFileComponent extends AttachFileTableBaseComponent implements OnInit {
  @ViewChild(SimpleTableComponent) simpleTableRef: SimpleTableComponent;
  @Input() data: IAttachFileDialogDataSingleEntity;
  @Input() selectedFile: IAttachmentMergeFile | null;
  @Input() expand = false;
  @Output() fileSelectedChange = new EventEmitter<IAttachmentMergeFile>();

  entityLabel = 'Files';
  renderMode = runtimeEnvironment.renderMode;
  entitiesMeta: IEntityMetadata[];

  columnHeadings: ISimpleTableColumn[] = [];

  constructor(
    private filePreviewHelper: FilePreviewHelperService,
    private dialog: MatDialog,
    messageNotificationService: MessageNotificationService,
    transformBytesToMB: TransformBytesToMB,
    datePipe: DatePipe,
    ngZone: NgZone,
    serviceProviderService: ServiceProviderService,
    sharedServiceProviderServiceFactory: SharedServiceProviderServiceFactory,
    identityStorage: IdentityStorage,
    fileExtensionMatcherService: FileExtensionMatcherService,
    contentTypeMapperService: ContentTypeMapperService,
  ) {
    super(
      messageNotificationService,
      transformBytesToMB,
      datePipe,
      ngZone,
      serviceProviderService,
      sharedServiceProviderServiceFactory,
      identityStorage,
      fileExtensionMatcherService,
      contentTypeMapperService,
    );

    const serviceProvider = sharedServiceProviderServiceFactory.getServiceProviderService(identityStorage.get()?.namespace);
    serviceProvider
      .getEntityMetadataService()
      .getEntitiesMetadata()
      .subscribe((entities) => (this.entitiesMeta = entities));
  }
  private columnCellBuilder: (args: IDatasourceCellBuilderArgs) => ISimpleTableRow['cells'] = ({ file }) => super.buildTableRow(file).cells;

  ngOnInit(): void {
    this.setColumnConfig(this.data?.templateNamespace);

    switch (this.data.templateNamespace) {
      case InveniasNamespace.name:
        this.entityLabel = 'Documents';
        break;
      case BullhornNamespace.name:
        this.entityLabel = 'Files';
        break;
    }

    if (this.data?.entity) {
      this.buildTable(this.data.entity, this.data);
    }
  }

  refreshFiles($event): void {
    $event.stopPropagation();
    if (this.data?.entity) {
      this.buildTable(this.data.entity, this.data);
    }
  }

  clearFileSelection() {
    this.simpleTableRef?.clearSelection();
  }

  tableRowClick(file: IAttachmentMergeFile): void {
    this.setFileNamespace(file);
    this.fileSelectedChange.emit(file);
  }

  tableRowActionClick(event: { item: IAttachmentMergeFile; action: ActionMeta }): void {
    this.setFileNamespace(event.item);
    const { item, action } = event;
    if (action === 'action-preview-file') {
      this.openFilePreviewModal([item.namespace, this.data.entity.type, this.data.entity.id, item.id], item);
    }
  }

  fileSelected(event: IAttachmentMergeFile): void {
    this.fileSelectedChange.emit(event);
  }

  fileItemActionClick(event: { item: IAttachmentMergeFile; action: ActionMeta }): void {
    if (event.action === 'action-preview-file') {
      this.openFilePreviewModal(event.item.path, event.item);
    }
  }

  openFilePreviewModal(filePath: string[], fileData: IAttachmentMergeFile): void {
    this.filePreviewHelper.getFilePreviewDataFor(filePath, fileData).subscribe({
      next: (filePreviewData) => {
        this.dialog.open(FilePreviewModalComponent, {
          data: filePreviewData,
          width: this.renderMode === 'web-app' ? '50vw' : '90vw',
          maxWidth: '90vw',
          height: '90vh',
          autoFocus: 'dialog',
          restoreFocus: true,
          panelClass: this.identityStorage.get().namespace + '-namespace',
        });
      },
      error: (error) => {
        this.messageNotificationService.error('An error occurred while loading the file preview.', error);
      },
    });
  }

  getIcon(type: string) {
    return this.iconService.getIcon(type);
  }

  protected override buildTableRow(file: IAttachmentMergeFile): ISimpleTableRow {
    const builder = this.columnCellBuilder ?? (() => super.buildTableRow(file).cells);
    const actions = [
      {
        iconClasses: 'material-symbols-outlined',
        icon: 'document_search',
        title: 'Preview file',
        meta: 'action-preview-file',
      },
    ];
    const cells = builder({
      file,
      formatSize: (size?: number) => this.transformBytesToMB.transform(size),
      formatDate: (date?: number) => this.datePipe.transform(date, 'medium') ?? '',
    });
    cells.push({ actions: actions });
    return { cells, data: file };
  }

  private setFileNamespace(file: IAttachmentMergeFile) {
    if (!file) {
      return;
    }

    const identityNamespace = runtimeEnvironment.origin.namespace;
    switch (identityNamespace) {
      case BullhornNamespace.name:
      case InveniasNamespace.name:
      case SalesforceNamespace.name:
        file.namespace = identityNamespace;
        break;
    }
  }

  getEntityDisplayName(entityType: string): string {
    return this.entitiesMeta?.find((e) => e.name.toLowerCase() === entityType.toLowerCase())?.label || entityType;
  }

  findEntityMeta(type: string) {
    return this.entitiesMeta?.find((e) => e.name.toLowerCase() === type?.toLowerCase());
  }

  private setColumnConfig(namespace?: string): void {
    const config = DatasourceColumnProviderFactory.getConfig(namespace, this.renderMode, DatasourceTableIdentifier.AttachFile);

    this.columnCellBuilder = config.cellBuilder;
    this.columnHeadings = config.columns;
    this.columnHeadings.push({ title: 'Actions', width: '15%' });
  }

  protected onFilesLoaded(files: ISimpleTableRow[], data: IAttachFileDialogDataSingleEntity): void {
    this.selectTheOnlyFile(files, data);
  }

  private selectTheOnlyFile(files: ISimpleTableRow[], data: IAttachFileDialogDataSingleEntity) {
    const isOptional = data.sectionConfig.optional;
    const isOnlyFile = files.length === 1;

    if (!isOptional && isOnlyFile) {
      const firstFile = files[0];

      setTimeout(() => {
        if (this.simpleTableRef) {
          firstFile.selected = true;
          const selectedFile = firstFile.data as IAttachmentMergeFile;
          this.tableRowClick(selectedFile);
        }
      }, 0);
    }
  }
}
